import { findItemByIndex, getItemsLength, parseSuggestionsResponse, SuggestionsGroupData, SuggestionsItemData } from "@xapp/chat-widget-core";
import { useCallback, useEffect, useMemo, useState } from "react";
import { SuggestionCommand } from "../components/Input";
import { useSuggestionsFetch } from "./useSuggestionsFetch";

function norm(index: number, len: number): number {
    return (index + len) % len;
}

export function useSuggestions(search: string, context: readonly string[]): SuggestionsState {
    const [suggestions, setSuggestions] = useState<readonly SuggestionsGroupData[] | undefined>();
    const [suggestionIndex, setSuggestionIndex] = useState<number>(-1);

    const [query, setQuery] = useState<string | undefined>(search);
    const [contextState, setContextState] = useState<readonly string[] | undefined>(context);

    useEffect(() => {
        setQuery(search);
    }, [search]);

    useEffect(() => {
        setContextState(context);
    }, [context]);

    const suggestionItem = useMemo<SuggestionsItemData | undefined>(() => suggestions ? findItemByIndex(suggestions, suggestionIndex) : undefined, [suggestions, suggestionIndex]);

    const shift = useCallback((delta: number) => {
        if (suggestions) {
            const len = getItemsLength(suggestions);
            setSuggestionIndex(index => norm(index + delta + ((index < 0 && delta <= 0) ? 1 : 0), len));
        } else {
            setSuggestionIndex(-1);
        }
    }, [suggestions]);

    const execute = useCallback((cmd: SuggestionCommand) => {
        switch (cmd) {
            case "prev":
                shift(-1);
                break;
            case "next":
                shift(+1);
                break;
            case "cancel":
                setSuggestionIndex(-1);
                setQuery(undefined);
                setContextState(undefined);
                break;
        }
    }, [shift]);

    const suggestionsResponse = useSuggestionsFetch(query || "", contextState);
    const suggestionsData = suggestionsResponse.state === "success" ? suggestionsResponse.data : undefined;

    const raw = useMemo(() => {
        return suggestionsData ? 
            parseSuggestionsResponse(suggestionsData, -1).map(suggestion => {
                return {
                    heading: suggestion.heading,
                    items: suggestion.items.slice().reverse()
                }
            }) : undefined;
    }, [suggestionsData]);

    useEffect(() => {
        const prev = suggestionItem;
        setSuggestions(raw);

        if (!prev || !raw) {            
            setSuggestionIndex(-1);
        }
    }, [raw, suggestionItem]);

    return useMemo<SuggestionsState>(() => ({
        suggestions,
        index: suggestionIndex,
        execute,
        item: suggestionItem
    }), [suggestions, suggestionIndex, execute, suggestionItem]);
}

export interface SuggestionsState {
    readonly suggestions: readonly SuggestionsGroupData[] | undefined;
    readonly index: number;
    readonly item: SuggestionsItemData | undefined;
    execute(cmd: SuggestionCommand): void;
}